/**
 * One record on the board. The kit draws the frame -- the open control, the
 * focus ring, the drag grip and "Move…" -- and the workspace draws what the
 * record says (`children`: phrasing content only, no controls, because the
 * whole body is the button that opens the record).
 *
 * Drag: a pointer may grab anywhere on the card (a click without movement
 * still opens it); a keyboard picks it up from the grip (Space or Enter), so
 * Space on the open button never starts a drag.
 */
import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import MoveMenu from './MoveMenu';
import type { OfferedMove } from './transitions';

export interface WorkCardProps {
  id: string;
  column: string;
  /** The record's name, for every accessible name on the card. */
  name: string;
  selected: boolean;
  onOpen: () => void;
  moves: OfferedMove[];
  columnLabel: (key: string) => string;
  onMove: (move: OfferedMove) => void;
  /** No drag at all (a phone showing one column; or a host that turns it off). "Move…" stays. */
  dragDisabled?: boolean;
  /** Registers the open button, so focus can come back to it when the record closes. */
  openRef?: (el: HTMLButtonElement | null) => void;
  children: React.ReactNode;
}

export default function WorkCard({ id, column, name, selected, onOpen, moves, columnLabel, onMove, dragDisabled = false, openRef, children }: WorkCardProps) {
  const { setNodeRef, setActivatorNodeRef, listeners, attributes, isDragging } = useDraggable({
    id, data: { column }, disabled: dragDisabled,
  });
  const descId = `okb-card-${id}-desc`;
  /*
   * dnd-kit types its listeners as Function (SyntheticListenerMap); TypeScript 5 refuses that as a React handler
   * (TS2322, the CRA build's checker). Each is named and typed here: the pointer starts a drag from anywhere on the
   * card, the key only from the grip.
   */
  const onPointerDownDrag = listeners?.onPointerDown as ((e: React.PointerEvent<HTMLElement>) => void) | undefined;
  const onKeyDownDrag = listeners?.onKeyDown as ((e: React.KeyboardEvent<HTMLElement>) => void) | undefined;
  return (
    <li className="okb-cardwrap">
      <article
        ref={setNodeRef}
        className={`okb-card${selected ? ' is-open' : ''}${isDragging ? ' is-dragging' : ''}`}
        data-card={id}
        onPointerDown={dragDisabled || !onPointerDownDrag ? undefined : (e) => onPointerDownDrag(e)}
      >
        <button
          type="button"
          className="okb-card__open"
          ref={openRef}
          aria-label={`Open ${name}`}
          aria-describedby={descId}
          aria-current={selected ? 'true' : undefined}
          onClick={onOpen}
        >
          <span id={descId} className="okb-card__body">{children}</span>
        </button>
        <span className="okb-card__tools">
          {!dragDisabled && (
            <button
              type="button"
              ref={setActivatorNodeRef}
              className="okb-card__grip"
              {...attributes}
              onKeyDown={onKeyDownDrag ? (e) => onKeyDownDrag(e) : undefined}
              aria-label={`Drag ${name} to another column`}
              aria-roledescription="draggable card"
            >
              <span aria-hidden="true" className="okb-grip" />
            </button>
          )}
          <MoveMenu name={name} moves={moves} columnLabel={columnLabel} onChoose={onMove} />
        </span>
      </article>
    </li>
  );
}
